import type { MouseEvent } from 'react';
import {
  BarChart3,
  FileSpreadsheet,
  BookMarked,
  Boxes,
  Calculator,
  ClipboardCheck,
  FileText,
  Inbox,
  LayoutDashboard,
  Network,
  PackageCheck,
  PackageMinus,
  PackageOpen,
  ScrollText,
  Users,
  Warehouse,
  type LucideIcon,
} from 'lucide-react';

export interface NavItem {
  /** Bo'lim ildizi: marshrut shu yo'lda turadi va faollik shu bilan aniqlanadi. */
  to: string;
  /**
   * Menyuda bosilganda OCHILADIGAN sahifa, agar u ildizning o'zi bo'lmasa.
   * Masalan "Omborxona" ildizi `/warehouses` (mahsulotlar qidiruvi), lekin
   * menyudan kirilganda omborlar ro'yxati (`/warehouses/list`) ochiladi.
   * Ichki sahifa `to` ning ostida turgani uchun menyu elementi faol qoladi.
   */
  entry?: string;
  /** i18n kaliti — matn shu yerda emas, lug'atda turadi. */
  label: string;
  /**
   * Pastki qatordagi (`MobileTabBar`) QISQA yorliq kaliti. 360px da beshta
   * tugmaning har biriga ~72px tegadi va «Mahsulot tushumi» kesilib
   * qolardi. Ekran o'qigich va `title` baribir to'liq `label` ni oladi.
   */
  shortLabel?: string;
  icon: LucideIcon;
  /**
   * Ruxsat kodi `@eombor/contracts` dagi `PERMISSIONS` katalogidan.
   *
   * Ro'yxat berilsa — **kamida bittasi** yetarli ("yoki", "va" emas). Bu
   * ichida bir nechta ko'rinish bo'lgan bo'lim uchun: "Ombor" da tuzilma
   * (`warehouse.read`) va mahsulotlar (`stock.read`) yonma-yon turadi va
   * xodimda ulardan biri bo'lsa ham bo'limga kirish nuqtasi kerak.
   *
   * Havolaning ko'rinishi ruxsatlarni BIRLASHTIRMAYDI: bo'lim ichida har bir
   * ko'rinish o'z ruxsatini alohida tekshiradi.
   */
  permission?: string | readonly string[];
  /**
   * Bo'lim faqat BO'LINMASI BOR xodimga ko'rinadi.
   *
   * Ruxsat bilan hal qilib bo'lmaydi va bu ataylab: «Mening omborim»
   * huquqqa emas, ISH JOYIGA bog'liq. Skladchida `subdivision.read` bo'lishi
   * mumkin, lekin uning o'z bo'limi yo'q — menyuda hech qachon ochilmaydigan
   * havola turishi kerak emas.
   */
  requiresSubdivision?: true;
}

/**
 * Bo'lim ko'rinadimi — ruxsat ro'yxatidagi har qanday moslik yetarli.
 * `Sidebar` va `AppShell` bir xil qoidadan foydalanishi uchun shu yerda.
 */
export const navItemVisible = (
  item: NavItem,
  can: (permission: string) => boolean,
  /* Xodimga bo'lim biriktirilganmi. Standart `false`: javob kelmaguncha
     havola ko'rinmaydi — paydo bo'lib, keyin yo'qolib ketishdan ko'ra
     kechroq paydo bo'lgani yaxshiroq. */
  hasSubdivision = false,
): boolean => {
  if (item.requiresSubdivision === true && !hasSubdivision) return false;
  if (item.permission === undefined) return true;
  if (typeof item.permission === 'string') return can(item.permission);
  return item.permission.some(can);
};

/**
 * Yon paneldagi bo'limlar. Ruxsati yo'q element **umuman chizilmaydi** —
 * o'chirilgan yoki xiralashgan holda ham emas: ombor xodimi hech qachon
 * ochilmaydigan tugmani qidirib vaqt sarflamasligi kerak.
 *
 * Bu ko'rinish qaroridir, avtorizatsiya emas. Har bir so'rovni backend
 * baribir o'zi tekshiradi.
 */
export const NAV_ITEMS: readonly NavItem[] = [
  { to: '/', label: 'nav.dashboard', shortLabel: 'tabbar.short.dashboard', icon: LayoutDashboard },
  /* "Mening navbatim" — FAQAT buyurtmachiga.

     Ro'yxatning o'zi ruxsatlardan hisoblanadi (`InboxService`) va texnik
     jihatdan NOD, skladchi va buxgalter uchun ham to'ladi. Lekin ular ishni
     bu yerdan olmaydi: NOD talabnomani `/requisitions` dan, skladchi
     `/handover` va `/warehouses` dan, buxgalter `/accounting/surveys` dan
     ochadi. Ular uchun "Mening navbatim" o'sha ro'yxatlarning uchinchi
     nusxasi bo'lib qolardi — bir xil ish ikki joyda ko'rinsa, qaysi biri
     asosiy ekani yo'qoladi.

     `requisition.create` aynan buyurtmachini ajratadi: NOD da
     `requisition.approve.org`, skladchida `requisition.accept.warehouse`,
     buxgalterda esa umuman talabnoma yozish huquqi yo'q. */
  {
    to: '/inbox',
    label: 'nav.inbox',
    shortLabel: 'tabbar.short.inbox',
    icon: Inbox,
    permission: 'requisition.create',
  },
  /* Bitta "Ombor" bo'limi: tuzilma va undagi mahsulotlar bir KATAK haqida
     gapiradi — "A-03-01-01 da nima bor?" degan savolga javob berish uchun
     ikki bo'lim orasida yurish kerak emas. Katalog esa alohida qoladi:
     mahsulot turi omborga bog'liq emas va uning ruxsati ham global. */
  /* Bo'lim faqat ombor bilan ISHLAYDIGANLARGA ko'rinadi, uni ko'ra
     oladigan hammaga emas.

     Farqi muhim: stansiya buyurtmachisiga ombor qoldig'ini o'qish kerak —
     talabnoma yozayotganda mahsulot bor-yo'qligini ko'rish uchun. Lekin
     ombor uning ISH JOYI emas: u yerga kirib zona chizmaydi, kirim
     qilmaydi, katakdan katakka ko'chirmaydi. Unga ombor — talabnoma
     ichidagi maydon, alohida bo'lim emas.

     Shuning uchun menyu "o'qiy oladimi?" degan savolga emas, "shu yerda
     ish qiladimi?" degan savolga qaraydi. `stock.read` bu ro'yxatda
     ATAYLAB yo'q. */
  {
    to: '/warehouses',
    // Menyudan kirilganda birinchi — omborlar ro'yxati.
    entry: '/warehouses/list',
    label: 'nav.warehouse',
    icon: Warehouse,
    permission: [
      'warehouse.layout.edit',
      'warehouse.create',
      'warehouse.label.print',
      'stock.receive',
      'stock.move',
      'stock.issue',
    ],
  },
  /* Mahsulot tushumi — 1C moddiy hisobotlari. Bitta bo'lim, chunki bu
     hujjatning IKKI TOMONI: buxgalter yuklaydi, skladchi joylashtiradi.
     Ikkiga bo'linsa, "men yuborgan hujjat qayerda?" degan savolga javob
     topish uchun bo'limlar orasida yurish kerak bo'lardi. */
  {
    to: '/supply',
    label: 'nav.supply',
    shortLabel: 'tabbar.short.supply',
    icon: FileSpreadsheet,
    permission: ['stock.supply.upload', 'stock.supply.place'],
  },
  { to: '/catalog', label: 'nav.catalog', icon: BookMarked, permission: 'catalog.read' },
  {
    to: '/requisitions',
    label: 'nav.requisitions',
    shortLabel: 'tabbar.short.requisitions',
    icon: FileText,
    permission: 'requisition.read',
  },
  /* Berish ekrani skladchining KUNDALIK ishi va u talabnomalar ro'yxatidan
     alohida turadi: skladchi hujjatni qidirmaydi, u skanerlaydi. */
  { to: '/handover', label: 'nav.handover', icon: PackageCheck, permission: 'stock.issue' },
  /* So'rovnomalar `/accounting` dan OLDIN turadi: manzil bo'yicha bo'lim
     nomini qidiradigan joy (`AppShell`) birinchi mos kelganini oladi va
     `/accounting/surveys` "Buxgalteriya" deb nomlanib qolmasligi kerak. */
  {
    to: '/accounting/surveys',
    label: 'nav.surveys',
    shortLabel: 'tabbar.short.surveys',
    icon: ClipboardCheck,
    permission: 'accounting.survey.approve',
  },
  /* Rasxod aktlari — moddiy buxgalterning ish ro'yxati.
     `/accounting` dan OLDIN turadi: `AppShell` bo'lim nomini manzil
     bo'yicha birinchi mos kelganidan oladi va `/write-offs`
     «Buxgalteriya» deb nomlanib qolmasligi kerak. */
  {
    to: '/write-offs',
    label: 'nav.writeOffs',
    shortLabel: 'tabbar.short.writeOffs',
    icon: PackageMinus,
    permission: 'writeoff.read',
  },
  { to: '/accounting', label: 'nav.accounting', icon: Calculator, permission: 'accounting.doc.read' },
  /* Bo'linmalar — «kichik ombor» bo'limi.
     Bu ADMIN ekrani emas va shuning uchun `/admin` ostida turmaydi: bo'lim
     xodimi bu yerga kundalik savol bilan keladi — «bizning bo'limda nima
     bor?». Yaratish va tahrirlash tugmalari `subdivision.manage` bilan
     alohida yopiladi, ro'yxatning o'zi esa hammaga ochiq. */
  {
    to: '/subdivisions',
    label: 'nav.subdivisions',
    shortLabel: 'tabbar.short.subdivisions',
    icon: Boxes,
    permission: 'subdivision.read',
  },
  /* STATISTIKA — bo'linmalar bo'limining YONIDA, chunki u aynan shular
     haqida: korxona → bo'linma → ichki ombor kesimida kirim va rasxod.

     Ro'yxatning BOSHIDA turmaydi va bu ataylab. `NAV_ITEMS` tartibi
     telefondagi pastki qatorni ham belgilaydi (ilk to'rtta bo'lim), bu
     esa kundalik ISH uchun: skladchi u yerdan tushum va omborni ochadi.
     Statistika — o'qiladigan ekran, u bir tugmani siqib chiqarmasligi
     kerak. Rahbar uni menyudan ochadi va stol ustida ishlaydi.

     Paneldagi «Tahlil» bloklaridan farqi chuqurlikda: u yerda doiraning
     yig'ma raqamlari, bu yerda esa tuzilma bo'ylab pastga tushish.
     Ruxsat ikkovida AYNI (`report.view`) — kim yig'ma raqamni ko'rsa,
     uning kesimini ham ko'rishi kerak, aks holda raqamning qayerdan
     kelgani tekshirib bo'lmaydigan bo'lib qolardi. */
  {
    to: '/statistics',
    label: 'nav.statistics',
    shortLabel: 'tabbar.short.statistics',
    icon: BarChart3,
    permission: 'report.view',
  },
  /* «Mening omborim» — xodimning O'Z bo'limidagi qoldiq.
     `/subdivisions` dan farqi egalikda: u ro'yxat va u yerda boshqa
     bo'limlar ham bor; bu esa bitta bo'lim — xodimniki. Ruxsat qo'yilmagan,
     chunki ko'rinish ISH JOYIGA bog'liq: bo'limi bor har qanday xodim o'z
     omborini ko'rishi kerak, bo'limi yo'qiga esa havolaning ma'nosi yo'q. */
  {
    to: '/my-warehouse',
    label: 'nav.myWarehouse',
    shortLabel: 'tabbar.short.myWarehouse',
    icon: PackageOpen,
    requiresSubdivision: true,
  },
  { to: '/admin/org', label: 'nav.org', icon: Network, permission: 'org.unit.manage' },
  /* Xodimlar va rollar bitta savolga javob beradi: "kim nima qila oladi?"
     Rol berish uchun avval xodimni topish kerak, rolni tahrirlash uchun esa
     unga kim ega ekanini bilish kerak — ikki bo'lim orasida yurish shart
     bo'lmasin. Standart ko'rinish — xodimlar: rol berish kundalik ish,
     rolning o'zini tahrirlash esa kamdan-kam bo'ladi. */
  {
    to: '/admin/users',
    label: 'nav.access',
    icon: Users,
    permission: ['user.read', 'rbac.role.manage'],
  },
  { to: '/audit', label: 'nav.audit', icon: ScrollText, permission: 'audit.read' },
];

/** `/admin/roles` ochilganda `/admin/roles` yonadi, `/` esa faqat aynan `/` da. */
/** Menyu elementi bosilganda boriladigan manzil. */
export const navEntry = (item: NavItem): string => item.entry ?? item.to;

export const isNavItemActive = (to: string, pathname: string): boolean =>
  to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(`${to}/`);

/**
 * Xodimga KO'RINADIGAN bo'limlar.
 *
 * Uch joyda kerak — yon panel, pastki qator va menyu — va uchalasi bir xil
 * javob berishi shart: bo'lim biridayu ikkinchisida yo'q bo'lsa, xodim uni
 * "ba'zan bor, ba'zan yo'q" deb biladi.
 */
export const visibleNavItems = (
  can: (permission: string) => boolean,
  hasSubdivision = false,
): readonly NavItem[] => NAV_ITEMS.filter((item) => navItemVisible(item, can, hasSubdivision));

/**
 * Joriy manzil qaysi bo'limga tegishli.
 *
 * Ichma-ich manzillarda (`/accounting` va `/accounting/surveys`) faqat ENG
 * ANIQ bo'lim yutadi. Aks holda ikkala havola bir vaqtda yoritilar va xodim
 * qayerda turganini menyudan bilolmasdi.
 */
export const activeNavTo = (
  items: readonly NavItem[],
  pathname: string,
): string | undefined =>
  [...items]
    .sort((a, b) => b.to.length - a.to.length)
    .find((item) => isNavItemActive(item.to, pathname))?.to;

/**
 * Bosish SPA o'tishiga aylantirilsinmi.
 *
 * `false` — havola ODDIY `href` bo'lib ishlaydi: yangi oynada ochish
 * (⌘/Ctrl/Shift bosilgan yoki o'rta tugma) navigatsiya tomonidan
 * o'g'irlanmasligi kerak.
 */
export const isPlainLeftClick = (event: MouseEvent): boolean =>
  !event.defaultPrevented &&
  event.button === 0 &&
  !event.metaKey &&
  !event.ctrlKey &&
  !event.shiftKey &&
  !event.altKey;
